import { faCubes } from "@fortawesome/pro-solid-svg-icons"
import BuildIcon from "@mui/icons-material/Build"
import InfoIcon from "@mui/icons-material/Info"
import InventoryIcon from "@mui/icons-material/Inventory"
import { FormControlLabel, Grid, Switch } from "@mui/material"
import { useFormikContext } from "formik"
import { useEffect, useRef, useState } from "react"
import {
  StyledBox,
  StyledListItem,
  StyledListItemIcon,
  StyledListItemText,
  StyledTextField
} from "shared/styled/General"
import { technicalPackageValidation } from "../../TechnicalPackageValidation"
import { StyledFormGroup } from "./TechnicalPackageForm.styled"
import { Sidebar } from "components/sidedrawer/Sidebar2"
import { useTranslation } from "react-i18next"

const TechnicalPackageFormData = ({ equipmentTypesResult }) => {
  const { t } = useTranslation()
  const { values, errors, touched, handleBlur, handleChange, setFieldValue } = useFormikContext()

  const handleEquipmentChange = (isChecked, equipment) => {
    const equipmentTypeIsInList = values.equipmentTypes?.find((x) => x.equipmentTypeId === equipment.id)
    if (equipmentTypeIsInList) {
      const withAddedEquipment = values.equipmentTypes.map((equipmentType) => {
        if (equipmentType.equipmentTypeId === equipment.id) {
          return { ...equipmentType, added: isChecked }
        }
        return equipmentType
      })
      setFieldValue("equipmentTypes", withAddedEquipment)
    } else {
      setFieldValue("equipmentTypes", [
        ...values.equipmentTypes,
        { ...equipment, equipmentTypeId: equipment.id, added: isChecked }
      ])
    }
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <StyledBox>
          <StyledListItemText style={{ paddingBottom: "8px" }} primary={t("global.name", "Name")} />
        </StyledBox>

        <StyledTextField
          Autocomplete="off"
          fullWidth
          id="name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.name && errors.name ? true : false}
          helperText={touched.name && errors.name ? t("global.name_is_required", "Name is required") : null}
          variant="outlined"
          inputProps={{ disableUnderline: true }}
        />
      </Grid>
      <Grid item xs={12}>
        <StyledBox>
          <StyledListItemText
            style={{ paddingBottom: "8px" }}
            primary={t("global.description", "Description")}
          />
        </StyledBox>

        <StyledTextField
          autoComplete="off"
          fullWidth
          id="description"
          value={values.description}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.description && errors.description ? true : false}
          helperText={touched.description && errors.description ? errors.description : null}
          variant="outlined"
          InputProps={{ disableUnderline: true }}
        />
      </Grid>
      <Grid item xs={12}>
        <StyledBox>
          <StyledListItemText style={{ paddingBottom: "8px" }} primary={t("global.equipment", "Equipment")} />
        </StyledBox>
        <StyledFormGroup>
          {equipmentTypesResult?.data?.map((equipmentType, i) => {
            return (
              <FormControlLabel
                control={
                  <Switch
                    onChange={(event) => {
                      handleEquipmentChange(event.target.checked, equipmentType)
                    }}
                    checked={
                      values.equipmentTypes?.find((x) => x.equipmentTypeId === equipmentType.id)?.added ||
                      false
                    }
                  />
                }
                label={equipmentType.name}
              />
            )
          })}
        </StyledFormGroup>
      </Grid>
    </Grid>
  )
}

export const TechnicalPackageForm = ({
  technicalpackageData,
  equipmentTypesResult,
  onSubmit,
  editMode,
  addOrUpdateResultState,
  feedback
}) => {
  const initialValues = editMode
    ? technicalpackageData
    : {
        id: 0,
        name: "",
        description: "",
        equipmentTypes: []
      }
  const [initialFormikValues, setInitialFormikValues] = useState(initialValues)
  const formRef = useRef()
  const { t } = useTranslation()

  useEffect(() => {
    setInitialFormikValues(initialValues)
  }, [technicalpackageData, editMode])

  useEffect(() => {
    if (addOrUpdateResultState && addOrUpdateResultState.status === 2) {
      formRef?.current?.resetForm()
    }
  }, [addOrUpdateResultState])

  const handleSubmit = (values) => {
    onSubmit(values)
  }

  return (
    <Sidebar
      initialFormikValues={initialFormikValues}
      setInitialFormikValues={setInitialFormikValues}
      formResult={addOrUpdateResultState}
      handleSubmit={handleSubmit}
      validationSchema={technicalPackageValidation}
      formData={{
        formId: "TechnicalPackageList",
        icon: faCubes,
        titleNew: t("sysadmin.create_technology_package", "Create Technology Package"),
        subTitleNew: "",
        titleEdit: t("sysadmin.update_technology_package", "Update Technology Package"),
        subTitleEdit: "",
        submitText: t("sysadmin.add_technology_package", "Add Technology Package"),
        submitEditText: t("sysadmin.save_technology_package", "Save Technology Package")
      }}
      formRef={formRef}>
      {equipmentTypesResult.status === 2 ? (
        <TechnicalPackageFormData equipmentTypesResult={equipmentTypesResult} />
      ) : (
        t("global.loading", "Loading...")
      )}
    </Sidebar>
  )
}
